import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Stack,
    CircularProgress,
    Alert,
    Paper,
    Divider,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    getDoc,
    addDoc,
    serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from './firebaseConfig';
import PersonIcon from '@mui/icons-material/Person';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const AvailableValets = () => {
    const [valets, setValets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        fetchAvailableValets();

        // Check if valetId is present in the URL and user is not authenticated
        const valetId = searchParams.get('valetId');
        console.log('valetId', valetId);
        if (valetId && !auth.currentUser) {
            // Redirect unauthenticated users to guest form
            navigate(`/guest-form?valetId=${valetId}&mode=park`);
        }
    }, [searchParams, navigate]);

    const fetchAvailableValets = async () => {
        try {
            const valetsQuery = query(
                collection(db, 'users'),
                where('role', '==', 'valet'),
                where('status', '==', 'active')
            );

            const querySnapshot = await getDocs(valetsQuery);
            const availableValets = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));

            setValets(availableValets);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching valets:', err);
            setError('Failed to load available valets. Please try again.');
            setLoading(false);
        }
    };

    const generateOTP = () => {
        return Math.floor(100000 + Math.random() * 900000); // Generates a random 6-digit number
    };

    const handleSelectValet = async (valetId) => {
        try {
            const userId = auth.currentUser.uid;
            const valetDoc = await getDoc(doc(db, 'users', valetId));
            const valetName = valetDoc.exists()
                ? valetDoc.data().firstName
                : 'Valet';

            const conversationRef = await addDoc(
                collection(db, 'conversations'),
                {
                    userId,
                    valetId,
                    valetName,
                    createdAt: serverTimestamp(),
                    status: 'pending',
                    otp: generateOTP(),
                    carStatus: 'unparked',
                    time_inquiries: 0,
                }
            );

            // alert('Request sent to the valet. Waiting for acceptance.');
            navigate('/active-chat', {
                state: { conversationId: conversationRef.id, valetId },
            });
        } catch (err) {
            console.error('Error creating a new conversation:', err);
            setError('Failed to send request. Please try again.');
        }
    };

    if (loading) {
        return (
            <Box
                display='flex'
                justifyContent='center'
                alignItems='center'
                minHeight='400px'>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity='error'>{error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, maxWidth: '800px', mx: 'auto' }}>
            <Typography variant='h4' gutterBottom align='center' sx={{ mb: 4 }}>
                Available Valets
            </Typography>

            {valets.length === 0 ? (
                <Alert severity='info'>
                    No valets are currently available. Please try again later.
                </Alert>
            ) : (
                <Stack spacing={2}>
                    {valets.map((valet, index) => (
                        <Paper
                            key={valet.id}
                            elevation={2}
                            sx={{
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                    transition: 'background-color 0.3s',
                                },
                            }}>
                            <Card
                                sx={{
                                    backgroundColor: 'transparent',
                                    boxShadow: 'none',
                                }}>
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            width: '100%',
                                        }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                            }}>
                                            <PersonIcon
                                                sx={{
                                                    fontSize: 40,
                                                    color: 'primary.main',
                                                }}
                                            />
                                            <Box>
                                                <Typography variant='h6'>
                                                    {valet.firstName || 'Valet'}
                                                </Typography>
                                                <Typography
                                                    variant='body2'
                                                    color='success.main'
                                                    sx={{ fontWeight: 500 }}>
                                                    Available Now
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Button
                                            variant='contained'
                                            color='primary'
                                            onClick={() =>
                                                handleSelectValet(valet.id)
                                            }
                                            endIcon={<ArrowForwardIcon />}
                                            sx={{ minWidth: '140px' }}>
                                            Select
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                            {index < valets.length - 1 && (
                                <Divider sx={{ opacity: 0.5 }} />
                            )}
                        </Paper>
                    ))}
                </Stack>
            )}
        </Box>
    );
};

export default AvailableValets;
